import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, Mail, Lock, User, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tokenFromUrl] = useState(searchParams.get('token') || '');
  const [formData, setFormData] = useState({
    token: tokenFromUrl,
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (tokenFromUrl) {
      setFormData(prev => ({ ...prev, token: tokenFromUrl }));
    }
  }, [tokenFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!formData.token.trim()) {
      toast.error('Por favor ingresa el código de invitación');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Por favor ingresa tu nombre');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/invitations/accept`, {
        token: formData.token,
        password: formData.password,
        name: formData.name
      });
      
      setAccepted(true);
      toast.success(data.message || 'Invitación aceptada exitosamente');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al aceptar invitación');
      }
    } finally {
      setLoading(false);
    }
  };

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F6' }}>
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#2D3748' }}>
            ¡Cuenta Creada!
          </h1>
          <p className="text-gray-600 mb-4">
            Tu cuenta ha sido creada exitosamente.
            <br />
            Serás redirigido a la página de inicio de sesión...
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="mt-4"
            style={{ backgroundColor: '#5A7D6C' }}
          >
            Ir a Iniciar Sesión
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F9F8F6' }}>
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#5A7D6C' }}>
            REKORD
          </h1>
          <h2 className="text-xl font-semibold mb-2" style={{ color: '#2D3748' }}>
            Aceptar Invitación
          </h2>
          <p className="text-sm text-gray-600">
            Completa tus datos para crear tu cuenta
          </p>
        </div>

        {!tokenFromUrl && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Si recibiste un email de invitación, copia el código y pégalo aquí.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="token" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Código de Invitación
            </Label>
            <Input
              id="token"
              type="text"
              value={formData.token}
              onChange={(e) => setFormData({ ...formData, token: e.target.value })}
              placeholder="Ingresa el código de invitación"
              required
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              El código que recibiste por email
            </p>
          </div>

          <div>
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Nombre Completo
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Juan Pérez"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Contraseña
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Confirmar Contraseña
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Repite tu contraseña"
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            style={{ backgroundColor: '#5A7D6C' }}
          >
            {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </Button>

          <div className="text-center text-sm text-gray-600 mt-4">
            ¿Ya tienes cuenta?{' '}
            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="font-medium hover:underline"
              style={{ color: '#5A7D6C' }}
            >
              Iniciar Sesión
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
